import { resetPassword } from '@aws-amplify/auth';
import { Button, Stack, TextField, Typography, useMediaQuery } from '@mui/material';
import { emailRegex } from 'components/Drawer/variants/externalMembers/shared';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import TKHSecurityLogo from 'v2/assets/vector/TKHSecurityLogo';
import AuthLayout from 'v2/components/AuthLayout/AuthLayout';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import { HOME_PATH } from 'v2/paths';

const ForgotPassword: React.FC<React.PropsWithChildren> = () => {
  const { translation } = useTranslation();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbarContext();
  const [email, setEmail] = useState<string>('');
  const [isEmailSend, setIsEmailSend] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [emailInputValidation, setEmailInputValidation] = useState<string | undefined>(undefined);

  const handleForgotPassword = async () => {
    setShowValidation(true);
    if (email && emailInputValidation === undefined) {
      try {
        await resetPassword({ username: email });
        setIsEmailSend(true);
      } catch (err) {
        showSnackbar(translation.change_password_error);
      }
    }
  };

  const handleGoBackToLoginPage = () => {
    navigate(HOME_PATH);
  };

  const isExtraSmallScreen = useMediaQuery('(min-width: 0px) and (max-width:627px)');
  const isSmallScreen = useMediaQuery('(min-width: 628px) and (max-width:643px)');
  const isMediumScreen = useMediaQuery('(min-width: 644px) and (max-width:663px)');

  const getPaperPadding = () => {
    if (isExtraSmallScreen || isSmallScreen) return '48px 24px';
    if (isMediumScreen) return '48px 40px';
    return '48px 80px';
  };

  const getPaperWidth = () => {
    if (isExtraSmallScreen) {
      return 'calc(100vw - 80px)';
    }
    if (isSmallScreen) {
      return '520px';
    }
    if (isMediumScreen) {
      return '552px';
    }
    return '632px';
  };

  useEffect(() => {
    if (email === '') setEmailInputValidation(translation.please_enter_an_email_address);
    else if (!email?.toLowerCase().match(emailRegex))
      setEmailInputValidation(translation.this_is_not_a_correct_email_address);
    else setEmailInputValidation(undefined);
  }, [email, translation.please_enter_an_email_address, translation.this_is_not_a_correct_email_address]);

  return (
    <AuthLayout paperPadding={getPaperPadding()} paperWidth={getPaperWidth()} windowId="forgot-password-window">
      <Stack flexDirection="column" gap="40px">
        <Stack flexDirection="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h4">ATLAS</Typography>
          <TKHSecurityLogo sx={{ height: 40, width: 106 }} />
        </Stack>
        {isEmailSend ? (
          <Stack flexDirection="column">
            <Typography variant="body2">{translation.check_your_email}</Typography>
            <Typography variant="body2">
              {translation.we_have_send_an_email_to} {email} {translation.with_a_link_to_reset_your_password}.
              <br />
              <br />
              {translation.no_email_in_inbox_or_spam_after_a_few_minutes}
            </Typography>
            <Button
              id="resend-email-button"
              onClick={handleForgotPassword}
              size="large"
              color="primary"
              style={{ marginTop: '40px' }}
            >
              {translation.resend_email}
            </Button>
          </Stack>
        ) : (
          <Stack flexDirection="column" gap="40px">
            <Typography variant="body2">{translation.enter_your_email_adress_to_reset_your_password}</Typography>
            <Stack flexDirection="column" gap="24px">
              <TextField
                id="email-input"
                type="email"
                label={translation.email_address}
                size="medium"
                variant="outlined"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                error={showValidation && emailInputValidation !== undefined}
                helperText={showValidation ? emailInputValidation : undefined}
              />
              <Button
                onClick={handleForgotPassword}
                variant="contained"
                size="large"
                color="primary"
                id="sign-in-button"
                disabled={emailInputValidation !== undefined && showValidation}
              >
                {translation.reset_password}
              </Button>
              <Button onClick={handleGoBackToLoginPage} size="large" id="sign-in-button">
                <Typography variant="button" color="textPrimary">
                  {translation.return_to_sign_in_page}
                </Typography>
              </Button>
            </Stack>
          </Stack>
        )}
      </Stack>
    </AuthLayout>
  );
};

export default ForgotPassword;
